<template>
    <div class="u-margin-top-30">
        <control :label="'Period ' + (index + 1)">
            <div class="a-form-inputs--half a-form-inputs--half-to">
                <div class="a-form-inputs__container">
                    <date-input v-model="periodStart"
                                name="periodStart"
                                v-field-control:periodStart.required="{type:'date'}"
                                @input="calcRate"
                    ></date-input>
                    <div class="a-checkbox u-margin-tb-10-30">
                        <input autocomplete="off" type="checkbox" v-model="includeFirstDay">
                        <label>Include first day</label>
                    </div>
                    <control-error :show="$error('periodStart') || !startPeriodValid()">
                        Care period must start after the onset of symptoms ({{ theDeceased.onsetOfSymptoms }})
                    </control-error>
                    <control-error :show="periodStartIsBeforeAttendanceRates()">
                        Care period data is unavailable prior to April 1st 2009
                    </control-error>
                    <control-error :show="!periodEnd || !periodStartIsBeforeEnd()">
                        Start of care period must be before end of care period
                    </control-error>
                </div>
                <div class="a-form-inputs__to">to</div>
                <div class="a-form-inputs__container">
                    <date-input v-model="periodEnd"
                                name="periodEnd"
                                v-field-control:periodEnd.required="{type:'date'}"
                                @input="calcRate"
                    ></date-input>
                    <div class="a-checkbox u-margin-tb-10-30">
                        <input autocomplete="off" type="checkbox" v-model="includeLastDay">
                        <label>Include last day</label>
                    </div>
                    <control-error :show="$error('periodEnd') || !endPeriodValid()">
                        Period end date must be before {{ theDeceased.dateOfDeath }}
                    </control-error>
                </div>
            </div>
        </control>
        <control label="Hours per Day">
            <div class="a-form-inputs--half a-form-inputs--half-to">
                <div class="a-form-inputs__container">
                    <input autocomplete="off" name="hours_per_day"
                           v-model="hoursPerDay"
                           v-field-control
                           required
                           min="0"
                           max="24"
                           type="number"
                           @input="calcRate">
                </div>
                <div class="a-form-inputs__to">at</div>
                <div class="a-form-inputs__container">
                    <select id="method" v-model="rate" required @change="calcRate">
                        <option value="basic">Basic Rate</option>
                        <option value="aggregate">Aggregate</option>
                    </select>
                </div>
            </div>
        </control>
        <control label="Gratuitous Care Discount">
            <div class="a-checkbox">
                <input autocomplete="off" type="checkbox" v-model="gratuitousCare" @change="calcRate">
                <label>Applies the 'conventional 25% discount' to reflect the gratuitous nature of care.</label>
            </div>
        </control>
        <control for="sub_total" label="Subtotal"
                 v-show="showSubtotal"
                 before-after="pound"
                 :tip-icon="subTotalIcon"
                 tip-text="Click to view calculation"
                 tip-side="left"
                 tip-width="140"
                 tip-name="sub_total"
                 :modal-name="'subTotal_' + uniqueId"
                 :endpoint="'detailedCare'"
                 :payload="{period : this.findPeriod(this.index)}">
            <input autocomplete="off" v-model="periodTotal" type="text" disabled>
        </control>

        <button v-if="showDelete" class="a-button" @click="deletePeriod">Delete This Period</button>

        <hr class="hr--margin-bottom">

        <modal :name="'subTotal_' + uniqueId" no-default-button="true">
            <modal-content></modal-content>
        </modal>

    </div>
</template>

<script>
    import _ from 'lodash';
    import moment from 'moment';
    import Control from 'components/form/Control.vue';
    import ControlError from 'components/form/control/Error.vue';
    import DateInput from 'components/form/control/DateInput.vue';
    import FormValidator from 'components/form/validator/mixin';
    import {mapGetters, mapActions} from 'vuex';
    import { setStepValid }  from 'services/routingMethods';
    import Modal from 'components/modal/Modal.vue';
    import ModalContent from 'components/modal/ModalContent.vue';

    const numberVal = value => parseFloat(value || 0);

    export default {
        name: 'care-period',
        props: {
            index: {
                type: Number,
                required: true
            },
            showSubtotal: {
                default: true
            },
            showDelete: {
                default: true
            },
            miniCalc: {
                default: false
            }
        },
        components: {
            Control,
            ControlError,
            DateInput,
            Modal,
            ModalContent
        },
        mixins: [
            FormValidator
        ],
        computed: {
            ...mapGetters('fatal/estate/headsOfLoss', ['findPeriod', 'allPeriodsValid']),
            ...mapGetters('fatal/estate/deceased', ['theDeceased']),
            ...mapGetters('fatal/estate/proceduralDates', {
                proceduralDatesValue: 'date'
            }),
            periodStart: {
                get(){
                    return this.findPeriod(this.index).periodStart;
                },
                set(value){
                    this.updatePeriodValue({key: 'periodStart', index: this.index, value});
                }
            },
            periodEnd: {
                get(){
                    return this.findPeriod(this.index).periodEnd;
                },
                set(value){
                    this.updatePeriodValue({key: 'periodEnd', index: this.index, value}).then(this.calcRate.bind(this));
                }
            },
            includeLastDay: {
                get(){
                    return this.findPeriod(this.index).includeLastDay;
                },
                set(value){
                    this.updatePeriodValue({key: 'includeLastDay', index: this.index, value}).then(this.calcRate.bind(this));
                }
            },
            includeFirstDay: {
                get(){
                    return this.findPeriod(this.index).includeFirstDay;
                },
                set(value){
                    this.updatePeriodValue({key: 'includeFirstDay', index: this.index, value});
                }
            },
            rate: {
                get(){
                    return this.findPeriod(this.index).rate;
                },
                set(value){
                    this.updatePeriodValue({key: 'rate', index: this.index, value});
                }
            },
            periodTotal: {
                get(){
                    return this.findPeriod(this.index).total.toFixed(2);
                },
                set(value){
                    this.updatePeriodValue({key: 'total', index: this.index, value});
                }
            },
            gratuitousCare: {
                get() {
                    return this.findPeriod(this.index).gratuitousCare;
                },
                set(value) {
                    this.updatePeriodValue({key: 'gratuitousCare', index: this.index, value});
                }
            },
            subTotalIcon: {
                get() {
                    return (this.findPeriod(this.index).periodStart !== '' &&
                    this.findPeriod(this.index).periodEnd !== '' &&
                    this.findPeriod(this.index).hoursPerDay > 0 )
                        ? 'formula' : 'default';
                }
            },
            hoursPerDay: {
                get() {
                    return this.findPeriod(this.index).hoursPerDay;
                },
                set(value) {
                    this.updatePeriodValue({key: 'hoursPerDay', index: this.index, value});
                }
            },
            uniqueId() {
                return this._uid;
            },
        },
        methods: {
            ...mapActions('fatal/estate/headsOfLoss', {
                updatePeriodValue: 'updatePeriodValue',
                deleteCarePeriod: 'deleteCarePeriod',
                calcCareSubTotalForPeriod: 'calcCareSubTotalForPeriod'
            }),
            deletePeriod() {
                this.deleteCarePeriod(this.index);
            },
            calcRate() {
                this.calcCareSubTotalForPeriod(this.index);
                this.$emit('update');
            },
            startPeriodValid() {
                return moment(this.periodStart, 'DD/MM/YYYY')
                    .isSameOrAfter(moment(this.theDeceased.onsetOfSymptoms, 'DD/MM/YYYY'));
            },
            endPeriodValid() {
                if(this.miniCalc) {
                    return;
                }
                let mPeriodEnd = moment(this.periodEnd, 'DD/MM/YYYY');
                return mPeriodEnd.isValid() 
                    ? mPeriodEnd.isSameOrBefore(moment(this.theDeceased.dateOfDeath, 'DD/MM/YYYY'))
                    : false;
            },
            periodStartIsBeforeEnd() {
                return this.periodEnd ? moment(this.periodStart, 'DD/MM/YYYY').isBefore(moment(this.periodEnd, 'DD/MM/YYYY')) : false;
            },
            periodStartIsBeforeAttendanceRates() {
                return moment(this.periodStart, 'DD/MM/YYYY').isBefore(moment('01/04/2009', 'DD/MM/YYYY'));
            }
        }
    }
</script>


// WEBPACK FOOTER //
// CarePeriod.vue?ce931066